// Игровой экран: противники сверху, сцена (стол / барахолка), моя панель, веер руки, лист запроса.
// Preact-компоненты; наружу — прежние строковые функции (renderToString), пока app.ts рендерит через innerHTML.
// Плюс одна DOM-функция раскладки веера (layoutHand).
import { h, Fragment } from 'preact';
import type { ComponentChildren, JSX } from 'preact';
import { render as toString } from 'preact-render-to-string';
import type { CardInst, CardRef, Game as GameState, Iid, Player, PlayerId, Prompt, PromptInfo, Score } from '../types.ts';
import { DB, card, maxLife, player, traitOf, turnOf, ddkPenalty, PLAYER_ZONES } from '../engine/state.ts';
import { legendMarketSizeOf, mutatorDefs } from '../engine/mutators.ts';
import { BOSSES } from '../engine/bosses.ts';
import { scorePlayer, priceFor, canActivate, chipsPayable, promptWho, canCancel, canUndo, undoLabel, undoReason, H, CHAOS_CARD } from '../engine/game.ts';
import { RULES } from '../engine/rules.ts';
import { SITUATIONAL_ACTIVATE } from '../engine/tokens.ts';
import { cardV, CardChipV, Txt, ChipIc, isTraitRebalanced } from './cards.ts';
import { cmdProps } from './commands.ts';
import type { ReplayOp, UiCommand } from './commands.ts';
import { Btn, Hint, cx } from './ui-kit.ts';
import type { V } from './ui-kit.ts';
import type { GameUi } from './state.ts';

/** Размер зоны: в представлении массив пуст, а размер лежит рядом в поле *Count */
export const cnt = (arr: readonly unknown[] | undefined, n: number | undefined): number => n ?? arr?.length ?? 0;
/** цвет полоски жизней: >20 — синий (бонус), 11–20 — зелёный, 6–10 — жёлтый, ≤5 — красный */
export const hpClass = (life: number): string => (life > 20 ? 'hp-blue' : life > 10 ? 'hp-green' : life > 5 ? 'hp-yellow' : 'hp-red');
export const handCount = (p: Player): number => p.handCount ?? p.hand.length;
const pct = (life: number, max: number): number => Math.max(0, Math.min(100, (life / max) * 100));

/** Очки игрока: в представлении считает сервер (у него все зоны, у клиента чужие руки пусты), в хотсите — движок */
export const scoreOf = (G: GameState, p: Player): Score => p.score ?? scorePlayer(G, p);
export const displayScore = (G: GameState, p: Player): number => scoreOf(G, p).total;

const SEARCH_ZONES = ['market', 'legendMarket', 'legendDeck', 'mainDeck', 'trash', 'chaosPile', 'limpPile'] as const;

/** Экземпляр карты по iid в любой видимой зоне (или ссылка из запроса) */
export function findCard(G: GameState, iid: Iid): CardRef | null {
  for (const p of G.players) for (const z of PLAYER_ZONES) {
    const c = p[z].find((x) => x.iid === iid);
    if (c) return c;
  }
  for (const z of SEARCH_ZONES) {
    const c = G[z].find((x) => x.iid === iid);
    if (c) return c;
  }
  const pr = G.prompt;
  if (pr) {
    if (pr.type === 'chooseCards') {
      const d = pr.defs.find((x) => x.iid === iid);
      if (d) return d;
    } else if (pr.type === 'defend') {
      if (pr.card && pr.card.iid === iid) return pr.card;
    } else {
      const d = pr.cards?.find((x) => x.iid === iid);
      if (d) return d;
    }
  }
  return null;
}

/** Кому сейчас нужно принять решение (или чей ход) */
export function needPid(G: GameState): PlayerId {
  const who = promptWho(G);
  if (who != null) return who;
  if (G.promptFor != null) return G.promptFor;
  return G.turn ? G.turn.player : 0;
}

/** Мой активный запрос (в хотсите — если телефон у того, кого спрашивают) */
export function myPrompt(G: GameState, me: PlayerId): Prompt | null {
  return G.prompt && promptWho(G) === me ? G.prompt : null;
}

export interface AffordInfo {
  price: number;
  /** сколько чипсин можно доплатить (легенды и фамильяры-легенды) */
  chipPay: number;
  afford: boolean;
  /** сколько чипсин не хватает мощи */
  needChips: number;
}

/** Могу ли я отменить своё незавершённое действие: в представлении считает сервер, в хотсите — движок */
export const cancellable = (G: GameState, me: PlayerId): boolean => G.canCancel ?? (canCancel(G, me) || canUndo(G, me));
/** Что откатит кнопка отмены: «розыгрыш карты», «покупку»… (для подписи) */
export const undoWhat = (G: GameState, me: PlayerId): string | null => (G.undoWhat !== undefined ? G.undoWhat : undoLabel(G, me));
/** Почему отмена недоступна (для подсказки в меню); null — доступна */
export const undoBlocked = (G: GameState, me: PlayerId): string | null => (G.undoBlocked !== undefined ? G.undoBlocked : undoReason(G, me));

/** Сколько свойств ☀ игрок может применить прямо сейчас (карты под контролем + жетоны); ситуативные «уничтожь эту карту» не в счёт */
export function activationCount(G: GameState, p: Player): number {
  if (!G.turn || G.turn.player !== p.id) return 0;
  const cards = [...p.inPlay, ...p.permanents].filter((c) => canActivate(G, c) && !SITUATIONAL_ACTIVATE.has(c.cid)).length;
  const tokens = H.tokenActivations(G, p).filter((a) => a.enabled !== false).length;
  return cards + tokens;
}

export function affordInfo(G: GameState, active: Player, c: CardRef): AffordInfo {
  const price = priceFor(G, active, c);
  const power = turnOf(G).power;
  const chipPay = chipsPayable(active, c) ? Math.min(active.chips, price) : 0;
  return { price, chipPay, afford: power >= price - chipPay, needChips: Math.max(0, price - power) };
}

// ---------- общие кусочки ----------
const ICard = (): V => h('i', { class: 'i-card' });

/** Полоска жизней; tag — div в аватарах, span внутри кнопок-плиток */
function Hp({ tag = 'div', life, max, num, fx }: { tag?: 'div' | 'span'; life: number; max: number; num: string; fx?: string }): V {
  return h(tag, { class: 'hp' },
    h(tag, { class: `hp-bar ${hpClass(life)}`, ...(fx ? { 'data-fx': fx, 'data-max': max } : {}), style: `width:${pct(life, max)}%` }),
    h('span', { class: 'hp-num' }, num));
}
const Badge = ({ cls, title, children }: { cls: string; title?: string; children?: ComponentChildren }): V =>
  h('span', { class: `av-badge ${cls}`, ...(title ? { title } : {}) }, children);

interface AvatarOpts {
  me: PlayerId;
  active: boolean;
  target: boolean;
  thinking: boolean;
}

function Avatar({ G, p, me, active, target, thinking }: { G: GameState; p: Player } & AvatarOpts): V {
  const max = maxLife(p);
  return h('div', { class: cx('av', active && 'active', target && 'target', thinking && 'thinking', p.id === me && 'mine', p.loser && 'loser'), 'data-pl': p.id, 'data-fx': `p:${p.id}`, role: 'button', ...cmdProps({ k: 'player', pid: p.id }) },
    h('div', { class: 'av-top' },
      p.boss ? h('img', { class: 'av-face', src: DB.cards[BOSSES[p.boss].familiar]?.art ?? '', alt: '' }) : null,
      h('span', { class: 'av-name' }, `${p.bot && !p.boss ? '🤖 ' : ''}${p.name}`),
      G.prizeOwner === p.id ? h(Badge, { cls: 'prize', title: 'главный приз' }, '🏆') : null,
      p.loser ? h(Badge, { cls: 'loser', title: 'лошара' }, '🐴') : null,
      thinking ? h(Badge, { cls: 'think' }, '⏳') : null),
    h(Hp, { life: p.life, max, num: `❤ ${p.life}`, fx: `hp:${p.id}` }),
    h('div', { class: 'av-st' },
      h('span', { title: 'чипсины' }, h(ChipIc, null), p.chips),
      h('span', { title: 'жетоны дохлых колдунов' }, `💀${p.ddk.length}`),
      h('span', { title: 'карт на руке' }, h(ICard, null), handCount(p)),
      p.permanents.length ? h('span', { title: 'постоянки' }, `∞${p.permanents.length}`) : null,
      h('span', { class: 'vp', title: 'победные очки' }, `★${displayScore(G, p)}`)));
}

/** Что известно о чужом запросе: в хотсите — сам запрос, в онлайне — promptInfo от сервера */
function promptInfoOf(G: GameState): PromptInfo | null {
  if (G.prompt) return { type: G.prompt.type, title: G.prompt.title ?? '', attacker: G.prompt.type === 'defend' ? G.prompt.attacker : null, card: G.prompt.type === 'defend' ? G.prompt.card : null };
  return G.promptInfo ?? null;
}

// ---------- плеер записи партии ----------
/** Панель плеера (src/ui/replay.ts): позиция в журнале партии, ход, воспроизведение */
export interface ReplayInfo {
  /** сколько действий журнала применено; first — начало партии (после подготовки), last — последнее перед итогом */
  pos: number;
  first: number;
  last: number;
  /** номер хода сейчас и всего ходов */
  turn: number;
  turns: number;
  playing: boolean;
  speed: number;
}

function ReplayBar({ r }: { r: ReplayInfo }): V {
  const rc = (op: ReplayOp): UiCommand => ({ k: 'replay', op });
  const atStart = r.pos <= r.first;
  return h('div', { class: 'replay-bar' },
    h('div', { class: 'rp-row' },
      h('input', { type: 'range', id: 'replay-seek', min: r.first, max: r.last, value: r.pos, 'aria-label': 'позиция в партии' }),
      h('span', { class: 'rp-pos' }, `ход ${r.turn} из ${r.turns}`)),
    h('div', { class: 'rp-row rp-btns' },
      h(Btn, { cls: 'icon ghost', id: 'rp-exit', title: 'выйти из просмотра', c: rc('exit') }, '✕'),
      h(Btn, { cls: 'icon', id: 'rp-prev-turn', title: 'к началу хода', c: rc('prevTurn'), disabled: atStart }, '⏮'),
      h(Btn, { cls: 'icon', id: 'rp-prev', title: 'шаг назад', c: rc('prev'), disabled: atStart }, '‹'),
      h(Btn, { cls: 'icon primary', id: 'rp-play', title: r.playing ? 'пауза' : 'смотреть', c: rc('toggle') }, r.playing ? '❚❚' : '▶'),
      h(Btn, { cls: 'icon', id: 'rp-next', title: 'шаг вперёд', c: rc('next') }, '›'),
      h(Btn, { cls: 'icon', id: 'rp-next-turn', title: 'следующий ход', c: rc('nextTurn') }, '⏭'),
      h(Btn, { cls: 'icon ghost rp-speed', id: 'rp-speed', title: 'скорость', c: rc('speed') }, `${r.speed}×`)));
}

// ---------- игровой экран ----------
/** replay — плеер записи: стол только для просмотра (глазами ui.viewer), внизу панель плеера */
export function GameScreen({ G, ui, replay = null }: { G: GameState; ui: GameUi; replay?: ReplayInfo | null }): V {
  const turn = turnOf(G);
  const me = player(G, ui.viewer);
  const active = player(G, turn.player);
  const isActive = me.id === active.id;
  const need = needPid(G);
  const pr = replay ? null : myPrompt(G, me.id);
  const canAct = !replay && isActive && !G.prompt && G.promptFor == null;
  const spectate = !isActive;
  const targets = pr && pr.type === 'choosePlayer' ? new Set(pr.candidates) : new Set<PlayerId>();
  const n = G.players.length;
  const opps: Player[] = [];
  for (let i = 1; i < n; i++) opps.push(player(G, (me.id + i) % n));
  const who = promptWho(G);
  const defending = pr && pr.type === 'defend' ? pr : null;

  // ---- строка статуса ----
  let status: V;
  if (replay) {
    // запись: решения и ходы — по именам (смотрящий ничего не решает)
    const decider = G.promptFor ?? (G.prompt ? need : null);
    status = decider != null
      ? h(Fragment, null, '⏳ ', h('b', null, player(G, decider).name), ` решает${G.prompt?.title ? `: ${G.prompt.title}` : ''}`)
      : h(Fragment, null, 'Ходит ', h('b', null, `${active.bot ? '🤖 ' : ''}${active.name}`));
  } else if (defending) status = h('span', { class: 'st-hot st-danger' }, '🛡 Защита');
  else if (pr) status = h('span', { class: 'st-hot' }, 'Твоё решение');
  else if (G.promptFor != null && G.promptFor !== me.id) status = h(Fragment, null, `⏳ ${player(G, G.promptFor).name} принимает решение…`);
  else if (need !== me.id && G.prompt) status = h(Fragment, null, `⏳ ${player(G, need).name} принимает решение…`);
  else if (isActive) status = h('span', { class: 'st-hot' }, 'Твой ход');
  else status = h(Fragment, null, 'Ходит ', h('b', null, `${active.bot ? '🤖 ' : ''}${active.name}`));

  // ---- баннер: ждём чужого решения (иначе кажется, что игра зависла) ----
  const waitPid = who != null && who !== me.id ? who : (G.promptFor != null && G.promptFor !== me.id ? G.promptFor : null);
  const info = promptInfoOf(G);
  let wait: V = null;
  if (waitPid != null && info) {
    const w = player(G, waitPid);
    let text: V;
    if (info.type === 'defend') {
      const attackerName = info.attacker != null ? player(G, info.attacker).name : 'Беспредел';
      const cardName = info.card ? card(info.card).name : '';
      text = h(Fragment, null, `⚔ ${info.attacker === me.id ? 'Ты атакуешь' : attackerName + ' атакует'} `, h('b', null, w.name), `${cardName ? ` картой «${cardName}»` : ''} — ${w.name} решает, защищаться ли`);
    } else text = h(Fragment, null, '⏳ ', h('b', null, w.name), ` принимает решение${info.title ? `: ${info.title}` : ''}`);
    wait = h('div', { class: 'alert wait' }, text, h('span', { class: 'dots' }, h('i', null), h('i', null), h('i', null)));
  }

  // ---- сцена: вкладки, мощь, стол / барахолка / мой стол ----
  const nAct = canAct ? activationCount(G, active) : 0;
  const tab = (mode: GameUi['mode'], label: ComponentChildren, extraCls = ''): V =>
    h('button', { class: cx('tab', ui.mode === mode && 'on', extraCls), 'data-mode': mode, ...cmdProps({ k: 'mode', mode }) }, label);
  const myMax = maxLife(me);
  const peek = spectate && !ui.handOpen;

  return h('div', { class: cx('game', spectate && 'spectate', pr && 'has-prompt', defending && 'defending'), 'data-mode': ui.mode },
    // ---- противники ----
    h('header', { class: 'top' },
      h('div', { class: 'opps', 'data-n': opps.length }, opps.map((p) => h(Avatar, { G, p, me: me.id, active: p.id === active.id, target: targets.has(p.id), thinking: G.promptFor === p.id || who === p.id }))),
      G.mutators?.length || G.daily
        ? h(Btn, { cls: 'icon mut', id: 'btn-mutators', title: G.daily ? 'сид дня' : 'мутаторы партии', c: { k: 'mutatorInfo' } }, G.mutators?.length ? mutatorDefs(G).map((m) => m.emoji).join('') : '📅')
        : null,
      h(Btn, { cls: 'icon', id: 'btn-fs', title: 'на весь экран', c: { k: 'fs' } }, '⛶'),
      replay
        ? h(Btn, { cls: 'icon', id: 'btn-replay-log', title: 'журнал партии', c: { k: 'replay', op: 'log' } }, '📜')
        : h(Btn, { cls: 'icon', id: 'btn-menu', title: 'меню', c: { k: 'menuOpen' } }, '☰')),
    // строка статуса: слева — чей ход / чьё решение, справа — мощь и чипсины активного игрока (на телефоне
    // это единственное место, где они помещаются целиком; вкладки ниже получают всю ширину)
    h('div', { class: cx('status', defending && 'danger') },
      h('div', { class: 'st-left' }, status),
      h('div', { class: cx('power', isActive && 'mine'), 'data-fx': 'power', title: 'мощь в этом ходу · чипсины (1 чипсина = 1 мощь при покупке легенды)' },
        h('span', { class: 'pw' }, '⚡', h('b', null, turn.power)),
        h('span', { class: 'ch', title: 'чипсины активного игрока' }, h(ChipIc, null), h('b', null, active.chips)),
        active.chips ? h('span', { class: 'sum', title: 'мощь + чипсины — доступно на легенду' }, `= ${turn.power + active.chips} на легенду`) : null,
        turn.legendDiscount ? h('span', { class: 'disc' }, `легенды −${turn.legendDiscount}`) : null)),
    wait,
    // ---- баннер: тебя атакуют / от тебя ждут решение в чужой ход ----
    defending
      ? h('div', { class: 'alert attack' }, '⚔ ', h('b', null, defending.attacker != null ? player(G, defending.attacker).name : 'Беспредел'), ` атакует тебя картой «${defending.card ? card(defending.card).name : 'Беспредел'}» — выбери защиту внизу`)
      : pr && spectate ? h('div', { class: 'alert ask' }, '⏳ Ход ', h('b', null, active.name), ` — от тебя ждут решение: ${pr.title ?? ''}`) : null,
    h('div', { class: 'stage' },
      h('div', { class: 'modebar' },
        h('div', { class: 'tabs' },
          // имя активного игрока в подписи вкладки — только на широком экране (на телефоне трём вкладкам и так тесно; имя есть в статусе)
          tab('play', h(Fragment, null, '🎴 Стол', spectate ? h('span', { class: 'tab-extra' }, ` ${active.name}`) : null, nAct ? h('span', { class: 'tab-badge', title: 'можно применить свойств ☀' }, `☀${nAct}`) : null), nAct ? 'glow' : ''),
          // в чужой ход — третья вкладка «Мой стол»: свои постоянки и жетоны можно рассмотреть, не дожидаясь своего хода
          spectate ? tab('mine', h(Fragment, null, '🏠 Мой', h('span', { class: 'tab-extra' }, ' стол'))) : null,
          tab('buy', '🛒 Барахолка')),
        // размеры колод — справочная мелочь: на широком экране справа от вкладок, на телефоне их нет (они есть в барахолке)
        h('div', { class: 'st-decks', title: 'основная колода · колода легенд · стопка жетонов дохлых колдунов · уничтожено' },
          h('span', { 'data-fx': 'deck:main' }, h(ICard, null), cnt(G.mainDeck, G.mainDeckCount)),
          h('span', { 'data-fx': 'deck:legend' }, `👑 ${cnt(G.legendDeck, G.legendDeckCount)}`),
          h('span', null, `💀 ${cnt(G.ddkPile, G.ddkPileCount)}`),
          h('span', { 'data-fx': 'trash' }, `🗑 ${G.trash.length}`))),
      h('div', { class: 'stage-body', id: 'stage-body' },
        h(TablePane, { G, active, me, isActive, canAct, spectate }),
        h('div', { class: 'pane pane-buy' }, h(Market, { G, active, canBuyNow: canAct, spectate })),
        spectate ? h(MinePane, { G, me }) : null),
      h(Preview, { G, ui, canPlay: canAct })),
    // ---- моя панель ----
    // сетка: жизни / плитки статов / кнопка хода. На широком экране кнопка занимает оба ряда справа, на телефоне
    // она стоит рядом с полоской жизней, а плитки получают всю ширину панели — крупные цели для пальца
    h('div', { class: cx('me', targets.has(me.id) && 'target', me.id === active.id && 'active'), 'data-pl': me.id, 'data-fx': `p:${me.id}`, 'data-drop': 'me', ...cmdProps({ k: 'player', pid: me.id }) },
      h('div', { class: 'me-hp' },
        h(Hp, { life: me.life, max: myMax, num: `❤ ${me.life}/${myMax}`, fx: `hp:${me.id}` }),
        G.prizeOwner === me.id ? h(Badge, { cls: 'prize', title: 'главный приз' }, '🏆') : null,
        me.loser ? h(Badge, { cls: 'loser', title: 'лошара' }, '🐴') : null),
      h('div', { class: 'av-st me-st' },
        h(Stat, { title: 'что такое чипсины', c: { k: 'chipsInfo' }, ic: h(Fragment, null, h(ChipIc, null), h('b', null, me.chips)), lbl: 'чипсины' }),
        h(Stat, { id: 'btn-tokens', title: 'жетон свойства и жетоны дохлых колдунов', c: { k: 'tokens' }, ic: h(Fragment, null, '💀', h('b', null, me.ddk.length)), lbl: 'жетоны' }),
        h(Stat, { id: 'btn-score', cls: 'vp', title: 'победные очки — разбивка', c: { k: 'score' }, ic: h(Fragment, null, '★', h('b', null, displayScore(G, me))), lbl: 'очки' }),
        h(Stat, { id: 'btn-deck', fx: `deck:${me.id}`, title: 'состав колоды', c: { k: 'deck' }, ic: h(Fragment, null, h(ICard, null), h('b', null, cnt(me.deck, me.deckCount))), lbl: 'колода' }),
        h(Stat, { id: 'btn-discard', fx: `discard:${me.id}`, title: 'карты в сбросе', c: { k: 'discard' }, ic: h(Fragment, null, '♻', h('b', null, me.discard.length)), lbl: 'сброс' })),
      h('div', { class: 'me-actions' },
        canAct ? h(Btn, { cls: 'primary', id: 'btn-end', c: { k: 'endTurn' } }, 'Конец хода', ui.autoEndAt ? h(AutoEndBar, { at: ui.autoEndAt }) : null)
          : spectate ? h(Btn, { cls: 'ghost', id: 'btn-handtoggle', c: { k: 'handToggle' } }, ui.handOpen ? 'Скрыть руку' : `Рука (${me.hand.length})`) : null),
      // шаг назад прямо на экране хода: действие уже доиграно, панели вопроса нет, а отменить ещё можно.
      // Кнопка плавает над панелью: в ряду с «Конец хода» она отбирала ширину у статов на телефоне
      canAct && cancellable(G, me.id)
        ? h(Btn, { cls: 'ghost cancel', id: 'btn-undo', title: `отменить ${undoWhat(G, me.id) ?? 'действие'} и вернуть всё как было (пока ничего не раскрыто)`, c: { k: 'cancel' } }, '↩ Отмена')
        : null),
    // ---- рука: пустая не занимает место — стол получает больше экрана ----
    h('div', { class: cx('hand', peek && 'peek', !me.hand.length && 'empty'), id: 'hand', style: `--n:${me.hand.length}` },
      me.hand.map((c, i) => h('div', { key: c.iid, class: cx('hslot', ui.raised === c.iid && 'raised'), style: `--i:${i}`, 'data-hslot': c.iid }, cardV({ inst: c }, { cls: 'mini', zone: 'hand' })))),
    // ---- лист запроса ----
    h('div', { class: cx('sheet', pr && `open pt-${pr.type}`, ui.sheetCollapsed && 'collapsed'), id: 'sheet' }, pr ? h(PromptSheet, { G, ui, pr }) : null),
    replay ? h(ReplayBar, { r: replay }) : null);
}
export function renderGame(G: GameState, ui: GameUi, replay: ReplayInfo | null = null): string {
  return toString(h(GameScreen, { G, ui, replay }));
}

/** Кнопка-статистика моей панели (чипсины, жетоны, очки, колода, сброс) */
function Stat({ id, cls, fx, title, c, ic, lbl }: { id?: string; cls?: string; fx?: string; title: string; c: Parameters<typeof cmdProps>[0]; ic: V; lbl: string }): V {
  return h('span', { class: cx('stat', cls), ...(id ? { id } : {}), ...(fx ? { 'data-fx': fx } : {}), role: 'button', title, ...cmdProps(c) },
    h('span', { class: 's-ic' }, ic), h('span', { class: 's-lbl' }, lbl));
}

/** Стол активного игрока: жетоны и постоянки, сыгранное в этот ход */
function TablePane({ G, active, me, isActive, canAct, spectate }: { G: GameState; active: Player; me: Player; isActive: boolean; canAct: boolean; spectate: boolean }): V {
  const turn = turnOf(G);
  const inPlay = [...active.permanents, ...active.inPlay];
  // жетоны активного игрока как карты-постоянки: свойство (с ☀, если активируемое) и постоянные/активируемые ЖДК
  const tokenCards = tokenCardsV(G, active, isActive && canAct);
  const tableCard = (c: CardInst, extraCls: string): V => {
    const act = isActive && canAct && canActivate(G, c);
    // ситуативная активация («уничтожь эту карту»): без подсветки и с приглушённой кнопкой
    const quiet = act && SITUATIONAL_ACTIVATE.has(c.cid);
    return cardV({ inst: c }, {
      cls: cx('mini', extraCls, quiet && 'act-quiet'), act: act && !quiet, used: turn.activated.includes(c.iid), zone: 'table',
      btnV: act ? h(Btn, { cls: cx('qp', quiet && 'quiet'), c: { k: 'activate', iid: c.iid } }, `☀ ${quiet ? 'Уничтожить и применить' : 'Применить'}`) : null,
    });
  };
  const empty = !inPlay.length && !tokenCards.length;
  return h('div', { class: 'pane pane-play', 'data-drop': 'table' },
    spectate ? h('div', { class: 'pane-title' }, 'Стол игрока ', h('b', null, active.name), active.id !== me.id ? ` · на руке: ${handCount(active)}` : '') : null,
    empty
      ? h('div', { class: 'table-empty' }, h('div', { class: 'drop-hint' }, isActive ? h(Fragment, null, 'Перетащи карту с руки сюда,', h('br', null), 'чтобы сыграть') : 'Пока ничего не сыграно'))
      : h('div', { class: 'table', id: 'table' },
        active.permanents.length || tokenCards.length
          ? h('div', { class: 'tgroup' }, h('div', { class: 'tlabel' }, 'Постоянки ∞ и жетоны'), h('div', { class: 'tcards' }, tokenCards, active.permanents.map((c) => tableCard(c, 'perm'))))
          : null,
        !inPlay.length && isActive ? h('div', { class: 'drop-hint slim' }, 'Перетащи карту с руки сюда, чтобы сыграть') : null,
        active.inPlay.length
          ? h('div', { class: 'tgroup' }, h('div', { class: 'tlabel' }, 'Сыграно в этот ход ', h('span', { class: 'sub' }, '· тап — прочитать')), h('div', { class: 'tcards' }, active.inPlay.map((c) => tableCard(c, ''))))
          : null));
}

/** Мой стол в чужой ход */
function MinePane({ G, me }: { G: GameState; me: Player }): V {
  const myTokens = tokenCardsV(G, me, false);
  const empty = !myTokens.length && !me.permanents.length && !me.inPlay.length;
  return h('div', { class: 'pane pane-mine' },
    h('div', { class: 'pane-title' }, `Мой стол · на руке: ${me.hand.length}`),
    empty
      ? h('div', { class: 'table-empty' }, h('div', { class: 'drop-hint' }, 'Постоянок пока нет'))
      : h('div', { class: 'table' },
        myTokens.length || me.permanents.length
          ? h('div', { class: 'tgroup' }, h('div', { class: 'tlabel' }, 'Постоянки ∞ и жетоны'), h('div', { class: 'tcards' }, myTokens, me.permanents.map((c) => cardV({ inst: c }, { cls: 'mini perm', zone: 'view', key: `mine:${c.iid}` }))))
          : null,
        me.inPlay.length
          ? h('div', { class: 'tgroup' }, h('div', { class: 'tlabel' }, 'Сыграно (карты с чужой колоды)'), h('div', { class: 'tcards' }, me.inPlay.map((c) => cardV({ inst: c }, { cls: 'mini', zone: 'view', key: `mine:${c.iid}` }))))
          : null));
}

/** Барахолка: карты, шальная магия, фамильяры активного игрока */
function Market({ G, active, canBuyNow, spectate }: { G: GameState; active: Player; canBuyNow: boolean; spectate: boolean }): V {
  const turn = turnOf(G);
  const affordOf = (c: CardRef): boolean | null => (canBuyNow ? affordInfo(G, active, c).afford : null);
  // от дорогих к дешёвым (доступное и так выделено в отдельный блок)
  const byPrice = (arr: readonly CardInst[]): CardInst[] => [...arr].sort((a, b) => priceFor(G, active, b) - priceFor(G, active, a));
  const chaosN = cnt(G.chaosPile, G.chaosPileCount);
  const chaosOut = chaosN === 0;
  const chaosAff = canBuyNow ? chaosN > 0 && turn.power >= RULES.chaosPrice : null;
  // пустая стопка — отдельный случай: карта не «не по карману», её просто больше нет
  const chaosV = (): V => cardV({ pile: 'chaos', cid: CHAOS_CARD }, { cls: cx('sm', chaosOut && 'out'), price: RULES.chaosPrice, afford: chaosOut ? null : chaosAff, count: chaosN, zone: 'market', tag: chaosOut ? 'стопка пуста' : '' });
  const mainV = (c: CardInst): V => cardV({ inst: c }, { cls: 'sm', price: priceFor(G, active, c), oldPrice: card(c).cost, chips: G.marketChips[c.iid] ?? 0, afford: affordOf(c), zone: 'market' });
  const fams = active.familiars;
  const famV = (c: CardInst, gap: boolean): V => cardV({ inst: c }, { cls: cx('sm', gap && 'gap'), price: priceFor(G, active, c), oldPrice: RULES.familiarPrice, afford: affordOf(c), zone: 'market', tag: chipsPayable(active, c) ? 'фамильяр · как легенда' : 'фамильяр' });
  const mainDeckSub = h('span', { class: 'sub' }, `осн. колода ${cnt(G.mainDeck, G.mainDeckCount)}`);
  const legDeckSub = h('span', { class: 'sub' }, `колода ${cnt(G.legendDeck, G.legendDeckCount)} · чипсины = мощь`);
  const rows: V[] = [];
  if (canBuyNow) {
    // мой ход: сверху — что по карману (обычные + шальная магия; легенды + фамильяры), внизу — остальное, приглушённо
    const mainOk = byPrice(G.market).filter((c) => affordOf(c));
    const mainNo = byPrice(G.market).filter((c) => !affordOf(c));
    const legOk = byPrice(G.legendMarket).filter((c) => affordOf(c));
    const legNo = byPrice(G.legendMarket).filter((c) => !affordOf(c));
    const famOk = fams.filter((c) => affordOf(c));
    const famNo = fams.filter((c) => !affordOf(c));
    const anyOk = mainOk.length || legOk.length || chaosAff || famOk.length;
    rows.push(h('div', { class: 'mrow-title ok' }, 'Можно купить ', h('span', { class: 'sub' }, `мощь ⚡ ${turn.power}`, active.chips ? h(Fragment, null, ' · чипсины ', h(ChipIc, null), active.chips) : null)));
    if (!anyOk) rows.push(h(Hint, null, 'Пока ничего не по карману — сыграй карты с руки, чтобы накопить мощь.'));
    if (mainOk.length || chaosAff) rows.push(h('div', { class: 'mrow' }, mainOk.map(mainV), chaosAff ? chaosV() : null));
    if (legOk.length || famOk.length) rows.push(h(Fragment, null, h('div', { class: 'mrow-title small' }, 'Легенды ', legDeckSub), h('div', { class: 'mrow' }, legOk.map(mainV), famOk.map((c, i) => famV(c, i === 0 && legOk.length > 0)))));
    const chaosNo = !chaosAff && !chaosOut;
    const anyNo = mainNo.length || legNo.length || chaosNo || famNo.length;
    if (chaosOut) rows.push(h(Hint, null, `Шальная магия кончилась — стопка пуста (все ${DB.byType['шальная магия']?.reduce((n, d) => n + (d.count || 1), 0) ?? 15} карт разобраны).`));
    if (anyNo) {
      rows.push(h('div', { class: 'mrow-title dim' }, 'Не хватает мощи ', mainDeckSub));
      if (mainNo.length || chaosNo) rows.push(h('div', { class: 'mrow dimrow' }, mainNo.map(mainV), chaosNo ? chaosV() : null));
      if (legNo.length || famNo.length) rows.push(h(Fragment, null, h('div', { class: 'mrow-title small dim' }, 'Легенды ', legOk.length ? null : legDeckSub), h('div', { class: 'mrow dimrow' }, legNo.map(mainV), famNo.map((c, i) => famV(c, i === 0 && legNo.length > 0)))));
    }
  } else {
    const chaosTop = G.legendMarket.length > legendMarketSizeOf(G);
    rows.push(h('div', { class: 'mrow-title' }, 'Барахолка ', mainDeckSub));
    rows.push(h('div', { class: 'mrow' }, byPrice(G.market).map(mainV), chaosTop ? chaosV() : null));
    rows.push(h('div', { class: 'mrow-title' }, 'Легенды ', legDeckSub));
    rows.push(h('div', { class: 'mrow' }, byPrice(G.legendMarket).map(mainV), fams.map((c, i) => famV(c, i === 0)), chaosTop ? null : chaosV()));
  }
  return h(Fragment, null, rows,
    h('div', { class: 'supply' }, 'Вялые палочки: ', h('b', null, cnt(G.limpPile, G.limpPileCount)), ' · запас чипсин: ', h('b', null, G.chipsSupply),
      ' · жетоны дохлых колдунов: ', h('b', null, cnt(G.ddkPile, G.ddkPileCount)), ' · уничтожено: ', h('b', null, G.trash.length),
      spectate && !fams.length && active.familiarsCount ? ` · фамильяров под планшетом: ${active.familiarsCount}` : ''));
}

// ---------- запросы ----------
/** Плитка игрока в выборе цели */
function PlayerTile({ G, p, pr }: { G: GameState; p: Player; pr: Extract<Prompt, { type: 'choosePlayer' }> }): V {
  const dmg = pr.damage?.[p.id];
  return h('button', { class: 'opt tile ptile', ...cmdProps({ k: 'pid', pid: p.id }) },
    h('span', { class: 'pt-name' }, p.name),
    h(Hp, { tag: 'span', life: p.life, max: maxLife(p), num: `❤ ${p.life}` }),
    h('span', { class: 'pt-st' },
      h('span', null, h(ChipIc, null), p.chips), h('span', null, `💀${p.ddk.length}`), h('span', null, h(ICard, null), handCount(p)),
      p.loser ? h('span', null, '🐴') : null, G.prizeOwner === p.id ? h('span', null, '🏆') : null,
      h('span', { class: 'vp', title: 'победные очки' }, `★${displayScore(G, p)}`),
      dmg != null ? h('span', { class: 'pt-dmg', title: 'ожидаемый урон' }, `−${dmg} ❤`) : null));
}

export function PromptSheet({ G, ui, pr }: { G: GameState; ui: GameUi; pr: Prompt }): V {
  const me = player(G, ui.viewer);
  let body: V = null;
  let actions: V = null;
  let title = pr.title ?? '';
  let sub = '';
  switch (pr.type) {
    case 'info':
      body = h(Txt, { cls: 'cardtext', text: pr.text });
      actions = h(Btn, { cls: 'primary', c: { k: 'ans', value: null } }, 'Ок');
      break;
    case 'chooseOption': {
      const n = pr.options.length;
      const yesNo = n === 2 && pr.options.some((o) => o.id === 'yes') && pr.options.some((o) => o.id === 'no');
      body = h('div', { class: 'opt-grid', 'data-n': n }, pr.options.map((o) =>
        h('button', { class: cx('opt tile', yesNo && (o.id === 'yes' ? 'primary' : 'ghost')), ...cmdProps({ k: 'opt', id: o.id }) }, o.label)));
      break;
    }
    case 'choosePlayer': {
      const others = pr.candidates.filter((pid) => pid !== me.id);
      const selfOk = pr.candidates.includes(me.id);
      const n = others.length + (pr.optional ? 1 : 0);
      sub = 'Нажми на игрока сверху или выбери здесь';
      const myDmg = pr.damage?.[me.id];
      body = h(Fragment, null,
        h('div', { class: 'opt-grid players', 'data-n': n },
          others.map((pid) => h(PlayerTile, { G, p: player(G, pid), pr })),
          pr.optional ? h('button', { class: 'opt tile ghost', ...cmdProps({ k: 'pid', pid: null }) }, 'Никого') : null),
        selfOk ? h('div', { class: 'self-opt' }, h('button', { class: 'ghost', ...cmdProps({ k: 'pid', pid: me.id }) },
          `${others.length ? 'Выбрать себя' : 'Себя (единственный вариант)'} `, h('small', null, `❤ ${me.life} · ★${displayScore(G, me)}`), ' ',
          myDmg != null ? h('span', { class: 'pt-dmg', title: 'ожидаемый урон' }, `−${myDmg} ❤`) : null)) : null);
      break;
    }
    case 'chooseNumber': {
      const range = pr.max - pr.min;
      if (range <= 7) {
        body = h('div', { class: 'opt-grid nums', 'data-n': range + 1 }, Array.from({ length: range + 1 }, (_, i) => pr.min + i).map((v) =>
          h('button', { class: 'opt tile num', ...cmdProps({ k: 'numPick', n: v }) }, v)));
      } else {
        const v = ui.numVal ?? pr.min;
        body = h(Fragment, null,
          h('div', { class: 'stepper' }, h(Btn, { c: { k: 'numStep', d: -1 } }, '−'), h('b', { id: 'numv' }, v), h(Btn, { c: { k: 'numStep', d: 1 } }, '+')),
          h('div', { class: 'hint center' }, `от ${pr.min} до ${pr.max}`));
        actions = h(Btn, { cls: 'primary', c: { k: 'numOk' } }, 'Ок');
      }
      break;
    }
    case 'chooseCards': {
      const cards: CardRef[] = pr.defs.length ? pr.defs : pr.cards.map((iid) => findCard(G, iid)).filter((c): c is CardRef => !!c);
      const nSel = ui.selected.size;
      sub = `выбери ${pr.min === pr.max ? pr.min : `от ${pr.min} до ${pr.max}`}${nSel ? ` · выбрано ${nSel}` : ''}`;
      // группируем по зонам (рука / сброс / стол / барахолка …), чтобы было понятно, откуда какая карта
      const groups = new Map<string, { label: string; cards: CardRef[] }>();
      // на уничтожение — от бесполезных к ценным: по ПО, затем по цене (первыми — те, что не жалко)
      const ordered = pr.kind === 'trash' ? [...cards].sort((a, b) => (card(a).vp ?? 0) - (card(b).vp ?? 0) || (card(a).cost ?? 0) - (card(b).cost ?? 0)) : cards;
      for (const c of ordered) {
        const z = zoneOf(G, me, c.iid);
        let g = groups.get(z.key);
        if (!g) {
          g = { label: z.label, cards: [] };
          groups.set(z.key, g);
        }
        g.cards.push(c);
      }
      // подпись — по реальной зоне карт («С руки», «Из сброса»); общая подпись запроса — только если зона неизвестна
      body = h(Fragment, null, [...groups.entries()].map(([key, g]) => h(Fragment, null,
        h('div', { class: 'pick-zone' }, key.startsWith(OTHER_ZONE) && pr.zoneLabel ? pr.zoneLabel : g.label, ' ', h('span', { class: 'sub' }, `· ${g.cards.length}`)),
        h('div', { class: 'pickrow' }, g.cards.map((c) => cardV({ inst: c }, { selected: ui.selected.has(c.iid), cls: 'sm pick', key: `pick:${c.iid}`, zone: 'pick', price: card(c).cost }))))));
      const ok = nSel >= pr.min && nSel <= pr.max;
      actions = h(Btn, { cls: 'primary', c: { k: 'cardsOk' }, disabled: !ok }, `Подтвердить${nSel ? ` (${nSel})` : ''}`);
      break;
    }
    case 'defend': {
      const attacker = pr.attacker != null ? player(G, pr.attacker) : null;
      title = attacker ? `${attacker.name} атакует тебя!` : 'Беспредел!';
      sub = 'Нажми карту защиты, чтобы сыграть её, или не защищайся';
      const cards = pr.cards.map((iid) => findCard(G, iid)).filter((c): c is CardRef => !!c);
      const cardName = pr.card ? card(pr.card).name : 'Беспредел';
      body = h(Fragment, null,
        h('div', { class: 'atk' },
          h('div', { class: 'atk-card' }, h(CardChipV, { cid: pr.card?.cid })),
          h('div', { class: 'atk-text' }, h('b', null, `«${cardName}»`), h(Txt, { cls: 'cardtext small', text: pr.text })),
          // ожидаемый урон — с учётом жетонов и постоянок атакующего (Тапки-донатки, «+4 палочками» и т.п.)
          pr.damage != null ? h('div', { class: 'atk-dmg', title: 'ожидаемый урон, если не защищаться' }, `−${pr.damage} ❤`, h('small', null, me.life - pr.damage < 1 ? 'ты подохнешь' : `останется ❤ ${me.life - pr.damage}`)) : null),
        h('div', { class: 'pickrow' }, cards.map((c) => cardV({ inst: c }, { cls: 'sm pick can-defend', key: `pick:${c.iid}`, zone: 'defend' }))));
      actions = h(Btn, { cls: 'danger', c: { k: 'ans', value: null } }, `Не защищаться${pr.damage != null ? ` (−${pr.damage})` : ''}`);
      break;
    }
  }
  // карты-контекст (раскрытая верхняя карта, беспредел и т.п.) — прямо в вопросе, без отдельного экрана
  const ctx = pr.type !== 'chooseCards' && pr.type !== 'defend' ? pr.cards ?? [] : [];
  // своя рука в чужой ход — кнопкой «Рука (N)» и вкладкой «Мой стол»; в вопрос карты попадают только как контекст (pr.cards)
  const meMax = maxLife(me);
  return h(Fragment, null,
    // компактная панель отвечающего над вопросом: решения зависят от жизней/чипсин/жетонов
    h('div', { class: 'sheet-me' },
      h('span', { class: 'sm-name' }, me.name),
      h(Hp, { tag: 'span', life: me.life, max: meMax, num: `❤ ${me.life}/${meMax}` }),
      h('span', { class: 'sm-st' }, h(ChipIc, null), `${me.chips} · 💀${me.ddk.length} · ★${displayScore(G, me)}`)),
    h('div', { class: 'sheet-bar' },
      cancellable(G, me.id)
        ? h(Btn, { cls: 'ghost cancel', id: 'btn-cancel', title: `отменить ${undoWhat(G, me.id) ?? 'действие'} и вернуть всё как было (пока ничего не раскрыто)`, c: { k: 'cancel' } }, '↩ Отмена')
        : null,
      h('div', { class: 'sheet-title' }, title),
      h(Btn, { cls: 'icon', id: 'btn-sheet-toggle', title: 'взглянуть на стол', c: { k: 'sheetToggle' } }, ui.sheetCollapsed ? '▲' : '👁')),
    h('div', { class: 'sheet-body' },
      ctx.length ? h('div', { class: 'pickrow ctx' }, ctx.map((c) => cardV({ inst: c }, { cls: 'sm', key: `ctx:${c.iid}`, zone: 'ctx', price: card(c).cost }))) : null,
      sub ? h(Hint, null, sub) : null,
      body),
    actions ? h('div', { class: 'sheet-foot' }, h('div', { class: 'actions' }, actions)) : null);
}
export function renderPrompt(G: GameState, ui: GameUi, pr: Prompt): string {
  return toString(h(PromptSheet, { G, ui, pr }));
}

/** Жетоны игрока как карты-постоянки на столе (жетон — свой текст без cid, потому не Card) */
function tokenCardsV(G: GameState, p: Player, canActNow: boolean, { onlyActive = false } = {}): V[] {
  const acts = H.tokenActivations(G, p);
  const out: V[] = [];
  const trait = traitOf(p);
  const usable = (a: { enabled?: boolean } | null): boolean => !!a && a.enabled !== false && canActNow;
  const tk = (id: string, title: string, text: string, act: { id: string; enabled?: boolean; hint?: string } | null, cls: string): V => {
    const enabled = !!act && act.enabled !== false && canActNow;
    return h('div', { class: cx('card tokencard', cls, enabled && 'act', act && act.enabled === false && 'cond'), 'data-zone': 'token', 'data-tok': id, 'data-fx': `tok:${id}`, title: act && act.hint ? act.hint : text },
      h('div', { class: 'tk-title' }, title),
      h('div', { class: 'tk-text' }, text),
      enabled && act ? h('div', { class: 'cardbtn' }, h(Btn, { cls: 'qp', c: { k: 'activateToken', id: act.id } }, '☀ Применить'))
        : act && canActNow ? h('div', { class: 'tk-cond' }, act.hint ?? 'условие не выполнено') : null);
  };
  if (trait) {
    const act = acts.find((a) => a.id.startsWith('trait')) ?? null;
    if (!onlyActive || usable(act)) out.push(tk('trait', `Жетон свойства${isTraitRebalanced(trait.id) ? ' ⚖' : ''}`, trait.text, act, 'tk-trait'));
  }
  for (const id of p.ddk) {
    const d = DB.ddk[id];
    if (!d) continue;
    const act = acts.find((a) => a.id === 'ddk-' + d.key) ?? null;
    if (onlyActive ? usable(act) : d.permanent || act) out.push(tk(id, `Жетон дохлого колдуна · ${ddkPenalty(d)} ПО`, d.text || '', act, 'tk-ddk'));
  }
  return out;
}
export function tokenCardsHtml(G: GameState, p: Player, canActNow: boolean, opts: { onlyActive?: boolean } = {}): string {
  return toString(h(Fragment, null, tokenCardsV(G, p, canActNow, opts)));
}

// Где лежит карта (для подписи в выборе карт)
const ZONES = [['hand', 'С руки', 0], ['discard', 'Из сброса', 1], ['inPlay', 'Со стола', 2], ['permanents', 'Постоянки', 3], ['deck', 'Из колоды', 4], ['familiars', 'Фамильяры', 5]] as const;
function zoneOf(G: GameState, me: Player, iid: Iid): { key: string; label: string } {
  for (const [z, label, order] of ZONES) if (me[z].some((c) => c.iid === iid)) return { key: order + ':' + z, label };
  for (const p of G.players) {
    if (p.id === me.id) continue;
    for (const [z, label, order] of ZONES) if (p[z].some((c) => c.iid === iid)) return { key: (10 + order) + ':' + p.id + z, label: `${label} — ${p.name}` };
  }
  if (G.market.some((c) => c.iid === iid)) return { key: '20:market', label: 'Барахолка' };
  if (G.legendMarket.some((c) => c.iid === iid)) return { key: '21:legend', label: 'Барахолка легенд' };
  if (G.legendDeck.some((c) => c.iid === iid)) return { key: '22:ldeck', label: 'Колода легенд' };
  if (G.trash.some((c) => c.iid === iid)) return { key: '23:trash', label: 'Уничтоженные' };
  return { key: OTHER_ZONE, label: 'Карты' };
}
const OTHER_ZONE = '30:other';

/** Раскладка веера руки (после рендера) */
export function layoutHand(root: ParentNode): void {
  const hand = root.querySelector<HTMLElement>('#hand');
  if (!hand) return;
  const slots = [...hand.querySelectorAll<HTMLElement>('.hslot')];
  const n = slots.length;
  const first = slots[0];
  if (!first) return;
  const W = hand.clientWidth;
  const firstCard = first.firstElementChild;
  const cw = (firstCard instanceof HTMLElement ? firstCard.offsetWidth : 0) || 120;
  const peek = hand.classList.contains('peek');
  const spacing = n > 1 ? Math.min(peek ? cw * 0.5 : cw + 8, (W - cw - 12) / (n - 1)) : 0;
  const total = cw + spacing * (n - 1);
  const x0 = (W - total) / 2;
  const mid = (n - 1) / 2;
  const overlap = n > 1 ? 1 - spacing / cw : 0;
  const maxRot = overlap > 0.05 ? Math.min(5, 28 / n) : 1.5;
  // слоты, созданные этой перерисовкой (без transform), встают на место без перехода: иначе при каждом
  // рендере (а во время чужих ходов их много) веер анимируется из (0,0) — рука «дрожит». Переход остаётся
  // для живых слотов: подъём/опускание карты, поворот экрана
  const fresh: HTMLElement[] = [];
  slots.forEach((s, i) => {
    const raised = s.classList.contains('raised');
    const x = x0 + i * spacing;
    const d = i - mid;
    const rot = raised || peek ? 0 : d * maxRot;
    const y = raised ? -30 : peek ? 0 : Math.abs(d) * Math.abs(d) * 1.4;
    if (!s.style.transform) { s.style.transition = 'none'; fresh.push(s); }
    s.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`;
    s.style.zIndex = String(raised ? 50 : 10 + i);
  });
  if (fresh.length) {
    void hand.offsetWidth; // применить позиции без перехода
    for (const s of fresh) s.style.transition = '';
  }
}

/** Предпросмотр поднятой карты */
export function previewV(c: CardRef, { canPlay }: { canPlay: boolean }): JSX.Element {
  const d = card(c);
  return h('div', { class: 'preview-card' },
    cardV({ inst: c }, { cls: 'big', key: `pv:${c.iid}`, zone: 'view' }),
    h('div', { class: 'preview-actions' },
      canPlay ? h(Btn, { cls: 'primary', c: { k: 'play', iid: c.iid } }, 'Сыграть') : null,
      h(Btn, { cls: 'ghost', c: { k: 'zoom', iid: c.iid } }, 'Подробнее'),
      h(Btn, { cls: 'ghost', c: { k: 'unraise' } }, '✕')),
    canPlay && d.attack ? h('div', { class: 'hint center' }, 'Перетащи на игрока, чтобы сразу выбрать цель') : null);
}
export const previewHtml = (c: CardRef, opts: { canPlay: boolean }): string => toString(previewV(c, opts));

/** Бокс превью над столом: поднятая карта руки (ui.raised) или пусто */
function Preview({ G, ui, canPlay }: { G: GameState; ui: GameUi; canPlay: boolean }): V {
  const me = player(G, ui.viewer);
  const c = ui.raised != null ? me.hand.find((x) => x.iid === ui.raised) : undefined;
  return h('div', { id: 'preview', class: c ? 'on' : '' }, c ? previewV(c, { canPlay }) : null);
}

/** Всё сыграно, купить нечего — полоска под «Конец хода» до автозавершения (кнопку можно нажать раньше) */
export const AUTO_END_MS = 3000;
function AutoEndBar({ at }: { at: number }): V {
  const elapsed = AUTO_END_MS - (at - Date.now());
  return h('div', { class: 'autoend', title: 'ход завершится автоматически' }, h('i', { style: `animation-duration:${AUTO_END_MS}ms;animation-delay:-${elapsed}ms` }));
}
